import { useCallback, useState } from "react";
import { Button, FormControl, Input, Stack, useToast, VStack } from "@chakra-ui/react";
import { useNavigate } from "react-router";
import { useAddWidgetAccountNameMutation, useRefreshQuotesMutation, useTransferInMutation } from "apis";
import { ActionBar, InputLabel, Layout, PageMotion, PoweredBy } from "components";
import { useAuth, useForm } from "hooks";
import ls from "utils/secureStorage";
import configs from "config";

const AddWidgetAccountName = () => {
  const toast = useToast();
  const [accountName, setAccountName] = useState("");

  const [_addAccountName, { isLoading }] = useAddWidgetAccountNameMutation();

  const navigate = useNavigate();
  const { auth } = useAuth();

  const handleAddAccountName = async (e: any) => {
    e.preventDefault();
    try {
      const response = await _addAccountName(accountName).unwrap();
      toast({
        position: "bottom-right",
        title: "Success",
        description: response?.message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      if (response) {
        const quoteId = quoteRes?.quote?.quoteId!;

        proceedToNextStep({ quoteId, token: auth?.token! });
      }
    } catch (error) {}
  };
  const { quoteRes, setQuoteRes, setTransferId, setTransferData } = useForm();
  const [refreshQuote, { isLoading: refreshLoading }] = useRefreshQuotesMutation();
  const [transferIn, { isLoading: transferLoading }] = useTransferInMutation();
  const tabType: "buy" | "sell" = ls.get("tabType");

  const proceedToNextStep = useCallback(async ({ quoteId, token }: { quoteId: string; token: string }) => {
    try {
      const refreshResponse = !quoteRes?.loginRequired ? quoteRes : await refreshQuote({ quoteId, token }).unwrap();

      if (refreshResponse && tabType === "sell") {
        navigate(`/payment`, { replace: true });

        return;
      }

      if (quoteId && refreshResponse) {
        setQuoteRes(refreshResponse);

        ls.set(configs.KYC_REQUIRED, refreshResponse.kyc.kycRequired);
        if (refreshResponse.kyc.kycRequired) {
          navigate("/kyc", { replace: true });
        } else {
          const transferResult = await transferIn({
            quoteId,
            token,
            type: "in",
          }).unwrap();

          setTransferId(transferResult?.transferId);
          setTransferData(transferResult);
          navigate(`/payment`, { replace: true });
        }
      }
    } catch (err: any) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageMotion key="Checkout" px="4px" minH={{ base: "96vh", md: "98vh" }}>
      <Layout>
        <ActionBar
          onBackClick={() => navigate(-1)}
          showLogo
          title="Account Name"
          _content={{ maxW: "370px" }}
          _textProp={{ fontSize: "16px", lineHeight: "22px", letterSpacing: "-0.15px" }}
        >
          To ensure your funds arrive safely and to protect against fraudulent activity, we require your full name to match the name
          associated with the bank account sending the deposit.
        </ActionBar>

        <Stack as="form" my="34px !important" px="3px" onSubmit={handleAddAccountName}>
          <FormControl>
            <InputLabel htmlFor="accountName">Account Name</InputLabel>
            <Input
              isRequired
              type="accountName"
              id="accountName"
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="JOHN DOE"
            />
          </FormControl>

          <VStack mt="40px !important">
            <Button
              minW={{ base: "auto", "2sm": "400px" }}
              type="submit"
              w="100%"
              isDisabled={isLoading || refreshLoading || transferLoading}
              isLoading={isLoading || refreshLoading || transferLoading}
              _hover={{
                _disabled: {
                  opacity: 0.3,
                },
              }}
            >
              Continue
            </Button>
          </VStack>
        </Stack>
      </Layout>

      <PoweredBy />
    </PageMotion>
  );
};

export default AddWidgetAccountName;
